// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — the country search box: type to filter, tap or Enter to guess. This
// is the whole input surface (principle #19 — a search + tap fits the hand far
// better than a D-pad or a reach-across map tap for a naming game).

import { searchCountries, type Country } from './data';
import { escapeHtml } from './dom';

export interface SearchConfig {
  container: HTMLElement;
  /** Ids legal to guess in the current mode. */
  pool: () => ReadonlySet<string>;
  /** Ids already guessed — shown greyed and not selectable. */
  taken: () => ReadonlySet<string>;
  onGuess: (id: string) => void;
  placeholder?: string;
}

export interface Search {
  focus(): void;
  clear(): void;
  refresh(): void;
  setDisabled(d: boolean): void;
  destroy(): void;
}

export function createSearch(cfg: SearchConfig): Search {
  const wrap = document.createElement('div');
  wrap.className = 'search';
  wrap.innerHTML = `
    <form class="search-form" novalidate autocomplete="off">
      <input class="search-input" type="text" inputmode="latin" autocomplete="off"
        autocapitalize="words" spellcheck="false" enterkeyhint="go"
        placeholder="${escapeHtml(cfg.placeholder ?? 'Guess a country…')}"
        aria-label="Guess a country" />
      <button class="search-go" type="submit" aria-label="Guess">Guess</button>
    </form>
    <ul class="search-list" role="listbox" aria-label="Matching countries"></ul>`;
  cfg.container.appendChild(wrap);

  const input = wrap.querySelector<HTMLInputElement>('.search-input')!;
  const list = wrap.querySelector<HTMLUListElement>('.search-list')!;
  const form = wrap.querySelector<HTMLFormElement>('.search-form')!;

  let results: Country[] = [];
  let active = -1;
  let disabled = false;

  function paint(): void {
    if (!input.value.trim() || disabled) {
      list.innerHTML = '';
      list.classList.remove('open');
      return;
    }
    const taken = cfg.taken();
    results = searchCountries(input.value, cfg.pool(), 8);
    if (!results.length) {
      list.innerHTML = `<li class="search-empty" aria-disabled="true">No country matches</li>`;
      list.classList.add('open');
      return;
    }
    if (active >= results.length) active = results.length - 1;
    list.innerHTML = results
      .map((c, i) => {
        const done = taken.has(c.id);
        return `<li class="search-item${i === active ? ' active' : ''}${done ? ' taken' : ''}"
          role="option" aria-selected="${i === active}" data-id="${c.id}">
          <span class="si-name">${escapeHtml(c.name)}</span>
          ${done ? '<span class="si-tag">guessed</span>' : ''}
        </li>`;
      })
      .join('');
    list.classList.add('open');
    for (const li of list.querySelectorAll<HTMLElement>('.search-item')) {
      li.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        pick(li.dataset.id!);
      });
    }
  }

  function pick(id: string): void {
    if (disabled) return;
    if (cfg.taken().has(id)) return;
    cfg.onGuess(id);
    input.value = '';
    active = -1;
    results = [];
    paint();
    input.focus();
  }

  function submit(): void {
    if (disabled) return;
    // Prefer the highlighted row, else the first non-taken match.
    const taken = cfg.taken();
    let choice: Country | undefined;
    if (active >= 0 && results[active] && !taken.has(results[active].id)) choice = results[active];
    else choice = results.find((c) => !taken.has(c.id));
    if (choice) pick(choice.id);
  }

  input.addEventListener('input', () => {
    active = -1;
    paint();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      active = Math.min(results.length - 1, active + 1);
      paint();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      active = Math.max(0, active - 1);
      paint();
    } else if (e.key === 'Escape') {
      input.value = '';
      active = -1;
      paint();
    }
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });

  return {
    focus: () => input.focus(),
    clear: () => {
      input.value = '';
      active = -1;
      paint();
    },
    refresh: paint,
    setDisabled(d: boolean) {
      disabled = d;
      input.disabled = d;
      (wrap.querySelector('.search-go') as HTMLButtonElement).disabled = d;
      if (d) {
        list.innerHTML = '';
        list.classList.remove('open');
      }
    },
    destroy: () => wrap.remove(),
  };
}
